import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";
import { type AdminReservation, ApiError, adminApi } from "../lib/api";
import { formatJstDateTime, todayYmdJst } from "../lib/format";

const SECTION_LABEL: Record<string, string> = {
  counter: "カウンター",
  zashiki: "掘りごたつ",
  any: "席指定なし",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "確認中",
  confirmed: "確定",
  cancelled: "キャンセル",
  completed: "ご来店済み",
  no_show: "ノーショー",
};

const SOURCE_LABEL: Record<string, string> = {
  liff: "LIFF",
  hotpepper: "ホットペッパー",
  manual: "手入力",
  phone: "電話",
};

export function AdminPage() {
  const listQuery = useQuery({
    queryKey: ["adminReservations"],
    queryFn: adminApi.reservations,
    refetchInterval: 60_000,
  });

  const grouped = useMemo(() => groupByDate(listQuery.data?.reservations ?? []), [listQuery.data]);

  if (listQuery.isLoading) {
    return (
      <PageLayout title="管理メニュー">
        <p className="text-sm text-neutral-500">読み込み中…</p>
      </PageLayout>
    );
  }
  if (listQuery.error) {
    const e = listQuery.error;
    if (e instanceof ApiError && e.status === 403) {
      return (
        <PageLayout title="管理メニュー">
          <p className="text-sm text-red-600">この機能は店舗オーナーのみご利用いただけます。</p>
          <Link to="/" className="mt-3 inline-block text-sm text-blue-600 underline">
            トップへ戻る
          </Link>
        </PageLayout>
      );
    }
    return (
      <PageLayout title="管理メニュー">
        <p className="text-sm text-red-600">予約一覧の取得に失敗しました。</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="管理メニュー">
      <div className="flex flex-col gap-6 pb-12">
        <Section title="今日" items={grouped.today} emptyText="今日のご予約はありません" />
        <Section title="明日" items={grouped.tomorrow} emptyText="明日のご予約はありません" />
        <Section title="以降" items={grouped.later} emptyText="以降のご予約はありません" />
      </div>
    </PageLayout>
  );
}

function Section({
  title,
  items,
  emptyText,
}: {
  title: string;
  items: AdminReservation[];
  emptyText: string;
}) {
  return (
    <section>
      <h2 className="mb-2 text-base font-semibold">
        {title}
        <span className="ml-2 text-xs text-neutral-500">{items.length}件</span>
      </h2>
      {items.length === 0 ? (
        <p className="text-xs text-neutral-500">{emptyText}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((r) => (
            <ReservationCard key={r.id} r={r} />
          ))}
        </div>
      )}
    </section>
  );
}

function ReservationCard({ r }: { r: AdminReservation }) {
  const isCancelled = r.status === "cancelled" || r.status === "no_show";
  return (
    <div
      className={
        isCancelled
          ? "rounded-md border border-neutral-200 bg-neutral-50 p-3 text-neutral-400"
          : "rounded-md border border-neutral-200 bg-white p-3"
      }
    >
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold">{formatJstDateTime(r.reservedAt)}</span>
        <span className="ml-2 rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
          {STATUS_LABEL[r.status] ?? r.status}
        </span>
      </div>
      <div className="mt-1 text-sm">
        <span className="font-medium">{r.contactName ?? "（名前未登録）"}</span>
        <span className="ml-2 text-neutral-600">
          {r.partySize}名 ／ {SECTION_LABEL[r.seatSection] ?? r.seatSection}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-neutral-500">
        {r.contactPhone ? (
          <a href={`tel:${r.contactPhone.replace(/-/g, "")}`} className="text-blue-600 underline">
            {r.contactPhone}
          </a>
        ) : null}
        <span>経由: {SOURCE_LABEL[r.source] ?? r.source}</span>
        {r.course ? <span>コース: {r.course}</span> : null}
      </div>
      {r.notes ? <p className="mt-1 text-xs text-neutral-500">要望: {r.notes}</p> : null}
    </div>
  );
}

function groupByDate(rows: AdminReservation[]): {
  today: AdminReservation[];
  tomorrow: AdminReservation[];
  later: AdminReservation[];
} {
  const today = todayYmdJst();
  const tomorrow = ymdAddDaysJst(today, 1);
  return rows.reduce(
    (acc, r) => {
      const ymd = ymdFromIsoJst(r.reservedAt);
      if (ymd === today) acc.today.push(r);
      else if (ymd === tomorrow) acc.tomorrow.push(r);
      else if (ymd > today) acc.later.push(r);
      return acc;
    },
    { today: [] as AdminReservation[], tomorrow: [] as AdminReservation[], later: [] as AdminReservation[] },
  );
}

function ymdFromIsoJst(iso: string): string {
  const d = new Date(iso);
  const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  return jst.toISOString().slice(0, 10);
}

function ymdAddDaysJst(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const date = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
